import { resolveEnv } from '@/config/environments';
import type { BffMenuResponse } from '@/types/bff';
import { credentialedFetch } from '@/utils/fetch';

/** Resolved once at module load — env vars are build-time constants. */
const { bffApiUrl } = resolveEnv();
const BFF_MENU_STATE_URL = `${bffApiUrl}/hanko-menu/v1/state`;

/**
 * Fetches the current menu state from the BFF API.
 *
 * - URL: `${bffApiUrl}/hanko-menu/v1/state` (resolved once at module level)
 * - Uses credentialed fetch with a 30-second timeout
 * - Retries up to 2 times on timeout/network errors (handles BFF cold starts)
 * - Sends the `Accept-Language` header (defaults to `'en-US'`)
 * - Throws on non-2xx responses or malformed JSON
 *
 * @param acceptLanguage - The value for the `Accept-Language` header (e.g. `'ja-JP'`). Defaults to `'en-US'`.
 * @returns The BFF menu response containing authentication state and menu items
 */
export async function fetchMenuState(acceptLanguage?: string): Promise<BffMenuResponse> {
  const maxRetries = 2;
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await credentialedFetch(BFF_MENU_STATE_URL, {
        headers: {
          'Accept-Language': acceptLanguage ?? 'en-US',
        },
        timeoutMs: 30_000, // BFF cold starts can take up to 30s
      });

      if (!response.ok) {
        throw new Error(`BFF API request failed with status ${response.status}`);
      }

      try {
        return (await response.json()) as BffMenuResponse;
      } catch {
        throw new Error('BFF API returned invalid JSON');
      }
    } catch (err) {
      lastError = err;

      // Only retry on timeout or network errors, not on HTTP error responses
      const isRetryable =
        err instanceof DOMException ||
        err instanceof TypeError ||
        (err instanceof Error && err.message.includes('timeout'));

      if (!isRetryable || attempt === maxRetries) {
        throw err;
      }
    }
  }

  // Should not reach here, but satisfy TypeScript
  throw lastError instanceof Error ? lastError : new Error(String(lastError ?? 'Unknown error'));
}
